import { getApiClient } from "@src/api";
import {
	type NavbarBranchItem,
	navbarBranchQueryKeys,
} from "@src/components/board/queries/fetch-ranklist";
import { trashQueryKeys } from "@src/components/board/queries/query-keys";
import {
	parseCardDocumentId,
	parseWhiteboardDocumentId,
} from "@src/components/collaboration/document-id/factories";
import { libraryQueryKeys } from "@src/components/library/queries/query-keys";
import type { LibraryCardNode } from "@src/components/library/schema";
import { graphql } from "@src/gql";
import { type CardPermission, CardStatus } from "@src/gql/graphql";
import { handleError } from "@src/utils/error-handling";
import {
	type QueryClient,
	useMutation,
	useQueryClient,
} from "@tanstack/react-query";

function typedCardIdString(cardId: string) {
	return parseCardDocumentId(cardId).toString();
}

function cardAggregateIdString(cardId: string) {
	return parseCardDocumentId(cardId).toIdString();
}

function whiteboardIdString(whiteboardId: string) {
	return parseWhiteboardDocumentId(whiteboardId).toIdString();
}

function invalidateCardQueries(queryClient: QueryClient, cardId: string) {
	const typedCardId = typedCardIdString(cardId);
	void queryClient.invalidateQueries({
		queryKey: libraryQueryKeys.myCard.byId(typedCardId),
	});
	void queryClient.invalidateQueries({
		queryKey: libraryQueryKeys.entities.all,
	});
	void queryClient.invalidateQueries({
		queryKey: libraryQueryKeys.entityTypeCounts.all,
	});
	void queryClient.invalidateQueries({
		queryKey: navbarBranchQueryKeys.all(),
	});
	void queryClient.invalidateQueries({
		queryKey: trashQueryKeys.all(),
	});
}

function setMyCardCacheStatus(
	queryClient: QueryClient,
	cardId: string,
	status: CardStatus,
) {
	const typedCardId = typedCardIdString(cardId);
	queryClient.setQueriesData<LibraryCardNode | null | undefined>(
		{ queryKey: libraryQueryKeys.myCard.byId(typedCardId) },
		(old) => (old ? { ...old, status } : old),
	);
}

export function setMyCardCacheKey(
	queryClient: QueryClient,
	cardId: string,
	key: string | null | undefined,
	name?: string | null,
) {
	if (!key) return;
	const typedCardId = typedCardIdString(cardId);
	const updateNode = (
		old: LibraryCardNode | null | undefined,
	): LibraryCardNode =>
		old
			? { ...old, key, ...(name != null ? { name } : {}) }
			: {
					id: typedCardId,
					key,
					name: name ?? null,
					status: CardStatus.Active,
				};

	queryClient.setQueriesData<LibraryCardNode | null | undefined>(
		{ queryKey: libraryQueryKeys.myCard.byId(typedCardId) },
		updateNode,
	);
	queryClient.setQueryData<LibraryCardNode | null | undefined>(
		libraryQueryKeys.myCard.ofId(typedCardId),
		updateNode,
	);
}

function throwIfGraphQlErrors(
	errors: readonly { message: string }[] | undefined,
	fallback: string,
) {
	if (errors?.length) {
		throw new Error(errors[0]?.message ?? fallback);
	}
}

const archiveCardMutation = graphql(/* GraphQL */ `
	mutation archiveCard($cardId: String!) {
		cards {
			archive(cardId: $cardId) {
				id
				commandId
				correlationId
				causationId
			}
		}
	}
`);

const deleteCardMutation = graphql(/* GraphQL */ `
	mutation deleteCard($cardId: String!) {
		cards {
			delete(cardId: $cardId) {
				id
				commandId
				correlationId
				causationId
			}
		}
	}
`);

const unarchiveCardMutation = graphql(/* GraphQL */ `
	mutation unarchiveCard($cardId: String!) {
		cards {
			unarchive(cardId: $cardId) {
				id
				commandId
				correlationId
				causationId
			}
		}
	}
`);

const publishCardMutation = graphql(/* GraphQL */ `
	mutation publishCard($cardId: String!, $initialize: Boolean) {
		cards {
			publish(cardId: $cardId, initialize: $initialize) {
				cardId
				key
				shareKey
				commandId
			}
		}
	}
`);

const unpublishCardMutation = graphql(/* GraphQL */ `
	mutation unpublishCard($cardId: String!) {
		cards {
			unpublish(cardId: $cardId) {
				id
				commandId
				correlationId
				causationId
			}
		}
	}
`);

const grantCardPermissionMutation = graphql(/* GraphQL */ `
	mutation grantCardPermission($cardId: String!, $email: String!, $role: CardPermission!, $initialize: Boolean) {
		cards {
			grantPermission(cardId: $cardId, email: $email, role: $role, initialize: $initialize) {
				id
				state {
					key
				}
				commandId
				correlationId
				causationId
			}
		}
	}
`);

const revokeCardPermissionMutation = graphql(/* GraphQL */ `
	mutation revokeCardPermission($cardId: String!, $email: String!) {
		cards {
			revokePermission(cardId: $cardId, email: $email) {
				id
				commandId
				correlationId
				causationId
			}
		}
	}
`);

const allowAnonymousEditCardMutation = graphql(/* GraphQL */ `
	mutation allowAnonymousEditCard($cardId: String!) {
		cards {
			allowAnonymousEdit(cardId: $cardId) {
				id
				commandId
			}
		}
	}
`);

const denyAnonymousEditCardMutation = graphql(/* GraphQL */ `
	mutation denyAnonymousEditCard($cardId: String!) {
		cards {
			denyAnonymousEdit(cardId: $cardId) {
				id
				commandId
			}
		}
	}
`);

const transferCardOwnershipMutation = graphql(/* GraphQL */ `
	mutation transferCardOwnership($cardId: String!, $newOwnerEmail: String!) {
		cards {
			transferOwnership(cardId: $cardId, newOwnerEmail: $newOwnerEmail) {
				id
				commandId
			}
		}
	}
`);

const renameCardMutation = graphql(/* GraphQL */ `
	mutation renameCard($cardId: String!, $name: String!) {
		cards {
			rename(cardId: $cardId, name: $name) {
				id
				state {
					key
				}
				commandId
			}
		}
	}
`);

const putToLibraryMutation = graphql(/* GraphQL */ `
	mutation putToLibrary($cardId: String!, $whiteboardId: String!) {
		cards {
			addToLibrary(cardId: $cardId, whiteboardId: $whiteboardId) {
				causationId
				commandId
				correlationId
				id
				state {
					key
				}
			}
		}
	}
`);

export type PutToLibraryParams = {
	cardId: string;
	whiteboardId: string;
};

export const useArchiveCard = () => {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (cardId: string) => {
			const result = await client.mutate({
				mutation: archiveCardMutation,
				variables: { cardId: cardAggregateIdString(cardId) },
			});

			throwIfGraphQlErrors(result.errors, "Failed to move card to trash");
			return result.data?.cards?.archive;
		},
		onSuccess: (_, cardId) => {
			setMyCardCacheStatus(queryClient, cardId, CardStatus.Archived);
			invalidateCardQueries(queryClient, cardId);
		},
	});
};

export const useUnarchiveCard = () => {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (cardId: string) => {
			const result = await client.mutate({
				mutation: unarchiveCardMutation,
				variables: { cardId: cardAggregateIdString(cardId) },
			});

			throwIfGraphQlErrors(result.errors, "Failed to restore card");
			return result.data?.cards?.unarchive;
		},
		onSuccess: (_, cardId) => {
			setMyCardCacheStatus(queryClient, cardId, CardStatus.Active);
			invalidateCardQueries(queryClient, cardId);
		},
	});
};

export const useDeleteCard = () => {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (cardId: string) => {
			const result = await client.mutate({
				mutation: deleteCardMutation,
				variables: { cardId: cardAggregateIdString(cardId) },
			});

			throwIfGraphQlErrors(result.errors, "Failed to delete card");
			return result.data?.cards?.delete;
		},
		onSuccess: (_, cardId) => {
			setMyCardCacheStatus(queryClient, cardId, CardStatus.Deleted);
			invalidateCardQueries(queryClient, cardId);
		},
	});
};

export const usePublishCard = () => {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			cardId,
			initialize,
		}: { cardId: string; initialize?: boolean }) => {
			const typedCardId = typedCardIdString(cardId);
			const result = await client.mutate({
				mutation: publishCardMutation,
				variables: { cardId: typedCardId, initialize },
			});

			throwIfGraphQlErrors(result.errors, "Failed to publish card");
			const published = result.data?.cards?.publish;
			if (!published) {
				throw new Error("Failed to publish card");
			}
			return published;
		},
		onSuccess: (data, variables) => {
			setMyCardCacheKey(queryClient, variables.cardId, data?.key);
			invalidateCardQueries(queryClient, variables.cardId);
		},
	});
};

export const useUnpublishCard = () => {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (cardId: string) => {
			const typedCardId = typedCardIdString(cardId);
			const result = await client.mutate({
				mutation: unpublishCardMutation,
				variables: { cardId: typedCardId },
			});

			throwIfGraphQlErrors(result.errors, "Failed to unpublish card");
			return result.data?.cards?.unpublish;
		},
		onSuccess: (_data, cardId) => {
			invalidateCardQueries(queryClient, cardId);
		},
	});
};

export const useGrantCardPermission = () => {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			cardId,
			email,
			role,
			initialize,
		}: {
			cardId: string;
			email: string;
			role: CardPermission;
			initialize?: boolean;
		}) => {
			const typedCardId = typedCardIdString(cardId);
			const result = await client.mutate({
				mutation: grantCardPermissionMutation,
				variables: {
					cardId: typedCardId,
					email,
					role,
					initialize,
				},
			});

			throwIfGraphQlErrors(result.errors, "Failed to grant card permission");
			return result.data?.cards?.grantPermission;
		},
		onSuccess: (data, { cardId }) => {
			setMyCardCacheKey(queryClient, cardId, data?.state?.key);
			invalidateCardQueries(queryClient, cardId);
		},
	});
};

export const useRevokeCardPermission = () => {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			cardId,
			email,
		}: { cardId: string; email: string }) => {
			const typedCardId = typedCardIdString(cardId);
			const result = await client.mutate({
				mutation: revokeCardPermissionMutation,
				variables: { cardId: typedCardId, email },
			});

			throwIfGraphQlErrors(result.errors, "Failed to revoke card permission");
			return result.data?.cards?.revokePermission;
		},
		onSuccess: (_, { cardId }) => {
			invalidateCardQueries(queryClient, cardId);
		},
	});
};

export const useAllowAnonymousEditCard = () => {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (cardId: string) => {
			const typedCardId = typedCardIdString(cardId);
			const result = await client.mutate({
				mutation: allowAnonymousEditCardMutation,
				variables: { cardId: typedCardId },
			});

			throwIfGraphQlErrors(
				result.errors,
				"Failed to allow anonymous edit for card",
			);
			return result.data?.cards?.allowAnonymousEdit;
		},
		onSuccess: (_, cardId) => {
			invalidateCardQueries(queryClient, cardId);
		},
	});
};

export const useDenyAnonymousEditCard = () => {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (cardId: string) => {
			const typedCardId = typedCardIdString(cardId);
			const result = await client.mutate({
				mutation: denyAnonymousEditCardMutation,
				variables: { cardId: typedCardId },
			});

			throwIfGraphQlErrors(
				result.errors,
				"Failed to deny anonymous edit for card",
			);
			return result.data?.cards?.denyAnonymousEdit;
		},
		onSuccess: (_, cardId) => {
			invalidateCardQueries(queryClient, cardId);
		},
	});
};

export const useTransferCardOwnership = () => {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			cardId,
			newOwnerEmail,
		}: { cardId: string; newOwnerEmail: string }) => {
			const typedCardId = typedCardIdString(cardId);
			const result = await client.mutate({
				mutation: transferCardOwnershipMutation,
				variables: { cardId: typedCardId, newOwnerEmail },
			});

			throwIfGraphQlErrors(result.errors, "Failed to transfer card ownership");
			return result.data?.cards?.transferOwnership;
		},
		onSuccess: (_, { cardId }) => {
			invalidateCardQueries(queryClient, cardId);
		},
	});
};

export const usePutToLibrary = () => {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (params: PutToLibraryParams) => {
			const typedCardId = typedCardIdString(params.cardId);
			const result = await client.mutate({
				mutation: putToLibraryMutation,
				variables: {
					cardId: typedCardId,
					whiteboardId: whiteboardIdString(params.whiteboardId),
				},
			});

			throwIfGraphQlErrors(result.errors, "Failed to add card to library");
			const added = result.data?.cards?.addToLibrary;
			if (!added) {
				throw new Error("Failed to add card to library");
			}
			return added;
		},
		onSuccess: (data, variables) => {
			setMyCardCacheKey(queryClient, variables.cardId, data?.state?.key);
			invalidateCardQueries(queryClient, variables.cardId);
		},
	});
};

export const useRenameCard = () => {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ cardId, name }: { cardId: string; name: string }) => {
			const result = await client.mutate({
				mutation: renameCardMutation,
				variables: {
					cardId: cardAggregateIdString(cardId),
					name,
				},
			});

			throwIfGraphQlErrors(result.errors, "Failed to rename card");
			return result.data?.cards?.rename;
		},
		onMutate: async ({ cardId, name }) => {
			const typedCardId = typedCardIdString(cardId);
			const aggregateId = cardAggregateIdString(cardId);

			await queryClient.cancelQueries({
				queryKey: libraryQueryKeys.myCard.byId(typedCardId),
			});
			await queryClient.cancelQueries({
				queryKey: libraryQueryKeys.entities.all,
			});
			await queryClient.cancelQueries({
				queryKey: navbarBranchQueryKeys.all(),
			});

			const previousMyCard = queryClient.getQueryData(
				libraryQueryKeys.myCard.ofId(typedCardId),
			);
			const previousEntities = queryClient.getQueriesData({
				queryKey: libraryQueryKeys.entities.all,
			});
			const previousNavbarBranches = queryClient.getQueriesData<
				NavbarBranchItem[]
			>({
				queryKey: navbarBranchQueryKeys.all(),
			});

			const matchesCardRef = (value: string | undefined) =>
				value === typedCardId || value === aggregateId;

			queryClient.setQueryData(
				libraryQueryKeys.myCard.ofId(typedCardId),
				(old: Record<string, unknown> | undefined) =>
					old ? { ...old, name } : old,
			);

			queryClient.setQueriesData(
				{ queryKey: libraryQueryKeys.entities.all },
				(old: unknown) => {
					if (!old || typeof old !== "object") return old;
					const infinite = old as {
						pages?: Array<{ nodes?: unknown[] }>;
					};
					if (!Array.isArray(infinite.pages)) return old;
					return {
						...infinite,
						pages: infinite.pages.map((page) => ({
							...page,
							nodes: Array.isArray(page.nodes)
								? page.nodes.map((item: unknown) => {
										if (typeof item === "object" && item !== null) {
											const card = item as {
												id?: string;
												entityId?: string;
												key?: string;
												name?: string;
											};
											if (
												matchesCardRef(card.id) ||
												matchesCardRef(card.entityId) ||
												matchesCardRef(card.key)
											) {
												return { ...card, name };
											}
										}
										return item;
									})
								: page.nodes,
						})),
					};
				},
			);

			queryClient.setQueriesData<NavbarBranchItem[]>(
				{ queryKey: navbarBranchQueryKeys.all() },
				(old) => {
					if (!old) return old;
					return old.map((item) =>
						matchesCardRef(item.key) ? { ...item, name } : item,
					);
				},
			);

			return {
				typedCardId,
				previousMyCard,
				previousEntities,
				previousNavbarBranches,
			};
		},
		onSuccess: (data, { cardId, name }) => {
			setMyCardCacheKey(queryClient, cardId, data?.state?.key, name);
		},
		onError: (error, _vars, context) => {
			if (context?.typedCardId && context.previousMyCard) {
				queryClient.setQueryData(
					libraryQueryKeys.myCard.ofId(context.typedCardId),
					context.previousMyCard,
				);
			}
			if (context?.previousEntities) {
				for (const [queryKey, data] of context.previousEntities) {
					queryClient.setQueryData(queryKey, data);
				}
			}
			if (context?.previousNavbarBranches) {
				for (const [queryKey, data] of context.previousNavbarBranches) {
					queryClient.setQueryData(queryKey, data);
				}
			}
			handleError(error, "Failed to rename card");
		},
		onSettled: (_, __, { cardId }) => {
			invalidateCardQueries(queryClient, cardId);
		},
	});
};
